import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

export const PaidVsOrganicTrendChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const breakdown = (day.breakdown as Array<Record<string, unknown>>) || [];
                let paid = 0;
                let organic = 0;

                for (const item of breakdown) {
                    if (item.type === 'REVENUE' && item.category === 'product_sale') {
                        const amount = Number(item.value) || 0;
                        const traffic = item.traffic as Record<string, unknown> | null;
                        if (traffic?.isPaid) {
                            paid += amount;
                        } else {
                            organic += amount;
                        }
                    }
                }

                return { date, paid, organic };
            });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <defs>
                            <linearGradient id="paidTrendGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.chart.line1} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={CHART_COLORS.chart.line1} stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="organicTrendGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.chart.line2} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={CHART_COLORS.chart.line2} stopOpacity={0.05} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(value, currencySymbol),
                                name === 'organic'
                                    ? t('charts.organic', 'Organic')
                                    : t('charts.paid', 'Paid'),
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Area
                            type="monotone"
                            dataKey="organic"
                            stackId="1"
                            stroke={CHART_COLORS.chart.line2}
                            fill="url(#organicTrendGrad)"
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                        />
                        <Area
                            type="monotone"
                            dataKey="paid"
                            stackId="1"
                            stroke={CHART_COLORS.chart.line1}
                            fill="url(#paidTrendGrad)"
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
